import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DollarSign } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { createEnrollment, getCourseById, Enrollment } from "@/utils/firestoreService";
import { Course } from "@/types/courseTypes";
import { useFirebase } from "@/contexts/FirebaseContext";
import { useAuth } from "@/contexts/AuthContext";
import { notifyNewEnrollment } from "@/utils/notificationService";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { PaymentDialog } from "@/components/payment/PaymentDialog";

const drivingExperienceOptions = [
  "Complete Beginner",
  "Some Experience",
  "Experienced",
  "Refresher Course"
];

const IndividualCourseEnrollment = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState<Course | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedHours, setSelectedHours] = useState(1);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [enrollmentId, setEnrollmentId] = useState("");
  const { currentUser } = useFirebase();
  const { user } = useAuth();

  const form = useForm({
    defaultValues: {
      drivingExperience: "",
      additionalNotes: "",
      vehicleType: "Manual",
      licenseType: "Provisional",
      previousLessons: "No",
      medicalConditions: false,
      medicalDetails: "",
      nervousAboutDriving: false,
      specificGoals: "",
      focusAreas: []
    }
  });

  useEffect(() => {
    const fetchCourse = async () => {
      if (!courseId) return;
      
      try {
        setLoading(true);
        const courseData = await getCourseById(courseId);
        setCourse(courseData);
        
        // Set fixed 3 hours for Car Hire courses
        if (courseData.title.toLowerCase().includes("car hire")) {
          setSelectedHours(3);
        }
      } catch (error) {
        console.error("Error fetching course:", error);
        toast({
          title: "Error",
          description: "Failed to load course details. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [courseId, toast]);

  const handleEnroll = async (formData: any) => {
    if (!courseId || !course) return;
    if (!currentUser) {
      toast({
        title: "Authentication Required",
        description: "Please log in to book a course",
        variant: "destructive"
      });
      return;
    }

    setProcessingPayment(true);
    
    try {
      toast({
        title: "Processing Enrollment",
        description: "Please wait while we process your enrollment..."
      });
      
      const studentName = user?.firstName && user?.lastName 
        ? `${user.firstName} ${user.lastName}`
        : currentUser.displayName || "Student";
      
      // Calculate the total amount based on price per hour and selected hours
      const totalAmount = course.pricePerHour ? course.pricePerHour * selectedHours : 0;
      
      // Create the enrollment with explicit values
      const enrollmentData: Omit<Enrollment, 'id' | 'enrolledAt' | 'updatedAt'> = {
        userId: currentUser.uid,
        studentName: studentName,
        studentEmail: currentUser.email || "student@example.com",
        courseId: courseId,
        courseName: course.title.toLowerCase().includes("blog") ? "Blog Lesson" : "Individual Lesson",
        courseType: "individual", // This is important for the merge logic
        status: "pending",
        enrollmentStatus: "pending",
        instructorId: "",
        instructorEmail: "",
        additionalNotes: formData.additionalNotes || "",
        drivingExperience: formData.drivingExperience || "",
        vehicleType: formData.vehicleType || "",
        licenseType: formData.licenseType || "",
        previousLessons: formData.previousLessons || "0",
        medicalConditions: formData.medicalConditions || false,
        medicalDetails: formData.medicalConditions ? formData.medicalDetails : "",
        nervousAboutDriving: formData.nervousAboutDriving || false,
        specificGoals: formData.specificGoals || "",
        focusAreas: formData.focusAreas || [],
        totalHours: selectedHours,
        hoursTaken: 0,
        hoursRemaining: selectedHours,
        amount: totalAmount,
        paymentStatus: "pending",
        isPartialPayment: false,
        amountPaid: 0,
        fullCoursePrice: totalAmount,
        remainingHours: selectedHours,
        startDate: new Date().toISOString(),
        preferredTime: "To be scheduled",
        currency: "GBP"
      };

      // Create enrollment first - this will handle merging with existing individual/blog lessons
      const createdEnrollment = await createEnrollment(enrollmentData);
      
      // Set the enrollment ID and show payment dialog
      setEnrollmentId(createdEnrollment.id);
      setShowPaymentDialog(true);
      
    } catch (error) {
      console.error("Error enrolling in course:", error);
      toast({
        title: "Enrollment Failed",
        description: "There was an error processing your enrollment. Please try again.",
        variant: "destructive"
      });
    } finally {
      setProcessingPayment(false);
    }
  };

  const handlePaymentComplete = (status: 'pending' | 'paid', method: 'cash' | 'card') => {
    setShowPaymentDialog(false);
    toast({
      title: "Enrollment Submitted",
      description: method === 'cash' 
        ? "Your booking is pending payment. Please complete the payment at our office."
        : "Your booking is pending admin verification. You will be notified when an instructor is assigned.",
    });
    navigate("/student-dashboard/my-enrollments");
  };

  if (loading) {
    return (
      <DashboardLayout role="student">
        <div className="flex justify-center items-center h-64">
          <p>Loading course details...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!course) {
    return (
      <DashboardLayout role="student">
        <div className="text-center py-12">
          <h2 className="text-xl font-bold mb-2">Course Not Found</h2>
          <p className="text-gray-500 mb-4">The course you're looking for could not be found.</p>
          <Button onClick={() => navigate("/student-dashboard/courses")}>
            Browse Courses
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="student">
      <div className="max-w-4xl mx-auto py-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate(`/student-dashboard/individual-course/${courseId}`)}
          className="mb-6"
        >
          Back to Course
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>{course.title}</CardTitle>
            <CardDescription>{course.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleEnroll)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label>Number of Hours</Label>
                      {course?.title.toLowerCase().includes("car hire") ? (
                        <div className="mt-2 text-lg font-semibold">
                          3 hours (Fixed duration for Car Hire)
                        </div>
                      ) : (
                        <div className="flex items-center space-x-4 mt-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setSelectedHours(Math.max(1, selectedHours - 1))}
                          >
                            -
                          </Button>
                          <span className="text-lg font-semibold">{selectedHours}</span>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setSelectedHours(selectedHours + 1)}
                          >
                            +
                          </Button>
                        </div>
                      )}
                    </div>

                    <div>
                      <Label>Price Per Hour</Label>
                      <div className="mt-2 text-lg font-semibold text-green-600">
                        £{course.pricePerHour?.toFixed(2)}/hour
                      </div>
                    </div>

                    <div>
                      <Label>Total Price</Label>
                      <div className="mt-2 text-lg font-semibold text-green-600">
                        £{(course.pricePerHour ? course.pricePerHour * selectedHours : 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Additional Information</h3>
                  
                  <FormField
                    control={form.control}
                    name="drivingExperience"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Driving Experience</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select your experience level" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {drivingExperienceOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="additionalNotes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Additional Notes</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Any specific requirements or preferences..."
                            className="resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={processingPayment}
                >
                  {processingPayment ? "Processing..." : "Book Now"}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
      
      {showPaymentDialog && (
        <PaymentDialog
          isOpen={showPaymentDialog}
          onClose={() => setShowPaymentDialog(false)}
          enrollmentId={enrollmentId}
          amount={course?.pricePerHour ? course.pricePerHour * selectedHours : 0}
          onPaymentComplete={handlePaymentComplete}
        />
      )}
    </DashboardLayout>
  );
};

export default IndividualCourseEnrollment; 